// TEMPORARY: Remove once React SPA migration is complete.
import type {UseMutationOptions, UseQueryOptions} from '@tanstack/react-query';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {fetcher, RequestMethod} from '#utils/http.js';

const REACT_PAGES_ENDPOINT = '/api/admin/react_pages';
const REACT_PAGES_V3_ENDPOINT = '/api/v3/react_pages';

export type PageConfig = {
  slug: string;
  label: string;
  path: string;
  enabled: boolean;
};

type ReactPagesApiResponse = {
  pages: PageConfig[];
};

type ReactPagesV3ApiResponse = {
  data: PageConfig[];
};

type ReactPagesResponse = PageConfig[];

type UpdateReactPagesRequest = {
  organizationId: number;
  pages: Record<string, boolean>;
};

type ReactPagesQueryOptions = Omit<
  UseQueryOptions<ReactPagesResponse>,
  'queryKey' | 'queryFn'
>;

type UpdateReactPagesMutationOptions = Omit<
  UseMutationOptions<unknown, Error, UpdateReactPagesRequest>,
  'mutationFn'
>;

export const reactPagesQueryKeys = {
  all: ['react_pages'] as const,
  currentOrg: () => [...reactPagesQueryKeys.all, 'current'] as const,
  byOrg: (orgId: number) => [...reactPagesQueryKeys.all, 'org', orgId] as const,
} as const;

export async function fetchReactPages(
  organizationId: number,
  signal?: AbortSignal
): Promise<ReactPagesResponse> {
  const response = await fetcher<ReactPagesApiResponse>(
    `${REACT_PAGES_ENDPOINT}?organization_id=${organizationId}`,
    {method: RequestMethod.GET, signal}
  );

  return response.pages;
}

export function reactPagesQueryOptions(
  organizationId: number,
  options?: ReactPagesQueryOptions
): UseQueryOptions<ReactPagesResponse> {
  return {
    queryKey: reactPagesQueryKeys.byOrg(organizationId),
    queryFn: ({signal}) => fetchReactPages(organizationId, signal),
    ...options,
  };
}

export function useReactPages(
  organizationId: number | null,
  options?: ReactPagesQueryOptions
) {
  return useQuery(
    reactPagesQueryOptions(organizationId!, {
      enabled: organizationId !== null,
      ...options,
    })
  );
}

async function updateReactPages(
  request: UpdateReactPagesRequest
): Promise<unknown> {
  return fetcher(REACT_PAGES_ENDPOINT, {
    method: RequestMethod.PUT,
    body: {
      organization_id: request.organizationId,
      pages: request.pages,
    },
  });
}

export function useUpdateReactPages(options?: UpdateReactPagesMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateReactPages,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: reactPagesQueryKeys.byOrg(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: reactPagesQueryKeys.currentOrg(),
      });
    },
    ...options,
  });
}

// --- Current org (non-admin) ---

export async function fetchCurrentOrgReactPages(
  signal?: AbortSignal
): Promise<ReactPagesResponse> {
  const response = await fetcher<ReactPagesV3ApiResponse>(
    REACT_PAGES_V3_ENDPOINT,
    {method: RequestMethod.GET, signal}
  );

  return response.data;
}

export function useCurrentOrgReactPages(options?: ReactPagesQueryOptions) {
  return useQuery({
    queryKey: reactPagesQueryKeys.currentOrg(),
    queryFn: ({signal}) => fetchCurrentOrgReactPages(signal),
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}
